/**
 * Detector de early media: monitorea el elemento `<audio id="telnyx-remote-audio">`
 * para detectar audio real del PSTN (tono de timbrado, buzón de voz, etc.).
 *
 * Cuando se detecta audio real proveniente de Telnyx, el timbrado sintético local
 * debe detenerse para no enmascarar:
 *  - El ringback real de la PSTN (sincronizado con el timbrado del destinatario).
 *  - El mensaje de buzón de voz ("deje su mensaje en la casilla de voz…").
 *
 * Usa `AnalyserNode` sobre el `MediaStream` del elemento de audio remoto.
 * Si el `AudioContext` está suspendido (p. ej. auto-llamada sin gesto), el detector
 * devuelve `false` y el flujo sigue igual que antes (sintético si puede, o silencio).
 */

import { devLog } from '@/lib/utils';
import {
  getRingbackOnlyAudioContext,
  getSharedAudioContext,
} from '@/lib/dtmfKeypad';

const AUDIO_ELEMENT_ID = 'telnyx-remote-audio';

/** Intervalo de muestreo (ms). 150 ms equilibra reactividad vs CPU. */
const CHECK_INTERVAL_MS = 150;

/**
 * Umbral RMS para considerar que hay audio real (vs silencio / ruido de fondo WebRTC).
 * Ringback PSTN típico tiene RMS ~0.02–0.10; silencio WebRTC < 0.002.
 */
const AUDIO_THRESHOLD = 0.006;

/**
 * Cuántos checks consecutivos con audio real se necesitan para confirmar early media.
 * Evita falsos positivos por picos de ruido aislados.
 */
const CONFIRM_COUNT = 2;

let analyser: AnalyserNode | null = null;
let sourceNode: MediaStreamAudioSourceNode | null = null;
let monitorInterval: ReturnType<typeof setInterval> | null = null;
let _hasRealAudio = false;
let connectedStreamId: string | null = null;
let consecutiveAboveThreshold = 0;

/**
 * Detección de silencio post-audio: si había audio real (ringback PSTN) y luego
 * el audio desaparece por varios checks consecutivos, significa que el operador
 * cortó el media (destino rechazó / buzón de voz terminó / etc.).
 *
 * Umbral: ~1.5s de silencio continuo tras haber tenido audio real.
 */
const SILENCE_CONFIRM_COUNT = 10; // 10 × 150ms = 1.5s de silencio
let consecutiveBelowThresholdAfterAudio = 0;
let _silenceAfterAudio = false;
let silenceCallback: (() => void) | null = null;

/* ───────── helpers ───────── */

function getAudioElement(): HTMLAudioElement | null {
  if (typeof document === 'undefined') return null;
  return document.getElementById(AUDIO_ELEMENT_ID) as HTMLAudioElement | null;
}

function getAvailableContext(): AudioContext | null {
  const rb = getRingbackOnlyAudioContext();
  if (rb && rb.state === 'running') return rb;
  const shared = getSharedAudioContext();
  if (shared && shared.state === 'running') return shared;
  return null;
}

function disconnectAnalyser(): void {
  try {
    sourceNode?.disconnect();
  } catch {
    /* ignore */
  }
  sourceNode = null;
  analyser = null;
  connectedStreamId = null;
}

/**
 * Conecta un `AnalyserNode` al `MediaStream` del `<audio>` remoto.
 * Retorna `true` si el analyser está listo para muestrear.
 */
function connectAnalyser(): boolean {
  const el = getAudioElement();
  if (!el) return false;

  const stream = el.srcObject as MediaStream | null;
  if (!stream) {
    disconnectAnalyser();
    return false;
  }

  const tracks = stream.getAudioTracks();
  if (tracks.length === 0 || tracks[0]!.readyState !== 'live') {
    disconnectAnalyser();
    return false;
  }

  // Ya conectado a este stream → reusar
  if (connectedStreamId === stream.id && analyser && sourceNode) return true;

  disconnectAnalyser();

  const ctx = getAvailableContext();
  if (!ctx) return false;

  try {
    sourceNode = ctx.createMediaStreamSource(stream);
    analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.3;
    sourceNode.connect(analyser);
    // NO conectar a ctx.destination → solo analizar, no duplicar audio
    connectedStreamId = stream.id;
    return true;
  } catch {
    disconnectAnalyser();
    return false;
  }
}

/** Retorna el nivel RMS del audio remoto (0 si no se puede medir). */
function checkAudioLevelRms(): number {
  if (!analyser) return 0;

  const data = new Float32Array(analyser.fftSize);
  try {
    analyser.getFloatTimeDomainData(data);
  } catch {
    return 0;
  }

  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    sum += data[i]! * data[i]!;
  }
  return Math.sqrt(sum / data.length);
}

/* ───────── API pública ───────── */

/**
 * `true` si se detecta audio real proveniente de Telnyx en el `<audio>` remoto.
 * Cuando esto es `true`, el timbrado sintético debe suprimirse.
 */
export function hasActiveRemoteAudio(): boolean {
  return _hasRealAudio;
}

/**
 * Inicia el monitoreo. Llamar al comenzar una sesión de timbrado.
 * Idempotente: si ya está corriendo, no duplica.
 */
export function startEarlyMediaMonitor(): void {
  if (monitorInterval) return;
  _hasRealAudio = false;
  consecutiveAboveThreshold = 0;

  let diagLogCount = 0;

  monitorInterval = setInterval(() => {
    const shouldLog = diagLogCount < 20 && diagLogCount % 5 === 0;
    diagLogCount++;

    const el = getAudioElement();
    const stream = el?.srcObject as MediaStream | null;
    const tracks = stream?.getAudioTracks() ?? [];
    const ctx = getAvailableContext();

    if (shouldLog) {
      devLog.log('[earlyMedia]', {
        hasElement: !!el,
        hasSrcObject: !!stream,
        audioTracks: tracks.length,
        trackState: tracks[0]?.readyState ?? 'none',
        trackEnabled: tracks[0]?.enabled ?? false,
        ctxState: ctx?.state ?? 'no-ctx',
        analyserConnected: !!analyser,
        hasRealAudio: _hasRealAudio,
      });
    }

    if (!connectAnalyser()) {
      return;
    }

    const level = checkAudioLevelRms();
    if (shouldLog) {
      devLog.log('[earlyMedia] RMS:', level.toFixed(5));
    }

    if (level > AUDIO_THRESHOLD) {
      consecutiveAboveThreshold++;
      consecutiveBelowThresholdAfterAudio = 0;
      if (consecutiveAboveThreshold >= CONFIRM_COUNT) {
        if (!_hasRealAudio) {
          devLog.log('[earlyMedia] ✓ Audio real detectado, suprimiendo sintético. RMS:', level.toFixed(4));
        }
        _hasRealAudio = true;
      }
    } else {
      consecutiveAboveThreshold = 0;
      // Si ya teníamos audio real y ahora hay silencio → contar
      if (_hasRealAudio) {
        consecutiveBelowThresholdAfterAudio++;
        if (consecutiveBelowThresholdAfterAudio >= SILENCE_CONFIRM_COUNT && !_silenceAfterAudio) {
          _silenceAfterAudio = true;
          devLog.log('[earlyMedia] ⚠ Silencio detectado tras audio real (destino probablemente rechazó). Checks:', consecutiveBelowThresholdAfterAudio);
          if (silenceCallback) silenceCallback();
        }
      }
    }
  }, CHECK_INTERVAL_MS);
}

/**
 * `true` si se detectó silencio después de haber tenido audio real.
 * Indica que el operador cortó el media (destino rechazó o buzón terminó).
 */
export function hasSilenceAfterAudio(): boolean {
  return _silenceAfterAudio;
}

/**
 * Registra un callback que se ejecuta UNA vez cuando se detecta silencio
 * después de audio real. Retorna función de limpieza.
 */
export function onSilenceAfterAudio(cb: () => void): () => void {
  silenceCallback = cb;
  return () => {
    if (silenceCallback === cb) silenceCallback = null;
  };
}

/**
 * Detiene el monitoreo. Llamar al terminar la sesión de timbrado.
 */
export function stopEarlyMediaMonitor(): void {
  if (monitorInterval) {
    clearInterval(monitorInterval);
    monitorInterval = null;
  }
  disconnectAnalyser();
  _hasRealAudio = false;
  _silenceAfterAudio = false;
  consecutiveAboveThreshold = 0;
  consecutiveBelowThresholdAfterAudio = 0;
  silenceCallback = null;
}
